/**
 * Utility functions to create SVG icon DOM elements for badges
 * Used in Contenteditable component for document, image, and video badges
 */

/**
 * Creates a document link icon SVG element
 * @returns SVG DOM element
 */
export const createDocumentLinkIcon = (): SVGElement => {
  const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  iconSvg.setAttribute(
    'style',
    'width:14px;height:14px;vertical-align:middle;margin-right:4px',
  )
  iconSvg.setAttribute('viewBox', '0 0 24 24')
  iconSvg.innerHTML =
    '<path fill="currentColor" d="M10.59 13.41c.41.39.41 1.03 0 1.42-.39.39-1.03.39-1.42 0a5.003 5.003 0 0 1 0-7.07l3.54-3.54a5.003 5.003 0 0 1 7.07 0 5.003 5.003 0 0 1 0 7.07l-1.49 1.49c.01-.82-.12-1.64-.4-2.42l.47-.48a2.982 2.982 0 0 0 0-4.24 2.982 2.982 0 0 0-4.24 0l-3.53 3.53a2.982 2.982 0 0 0 0 4.24m2.82-4.24c.39-.39 1.03-.39 1.42 0a5.003 5.003 0 0 1 0 7.07l-3.54 3.54a5.003 5.003 0 0 1-7.07 0 5.003 5.003 0 0 1 0-7.07l1.49-1.49c-.01.82.12 1.64.4 2.43l-.47.47a2.982 2.982 0 0 0 0 4.24 2.982 2.982 0 0 0 4.24 0l3.53-3.53a2.982 2.982 0 0 0 0-4.24.973.973 0 0 1 0-1.42"/>'
  return iconSvg
}

/**
 * Creates an image icon SVG element
 * @returns SVG DOM element
 */
export const createImageIcon = (): SVGElement => {
  const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  iconSvg.setAttribute(
    'style',
    'width:14px;height:14px;vertical-align:middle;margin-right:4px',
  )
  iconSvg.setAttribute('viewBox', '0 0 24 24')
  iconSvg.innerHTML =
    '<path fill="currentColor" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2M8.5 13.5l2.5 3.01L14.5 12l4.5 6H5z"/>'
  return iconSvg
}

/**
 * Creates a video icon SVG element
 * @returns SVG DOM element
 */
export const createVideoIcon = (): SVGElement => {
  const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  iconSvg.setAttribute(
    'style',
    'width:14px;height:14px;vertical-align:middle;margin-right:4px',
  )
  iconSvg.setAttribute('viewBox', '0 0 24 24')
  iconSvg.innerHTML =
    '<path fill="currentColor" d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2m-8 12.5v-9l6 4.5z"/>'
  return iconSvg
}

/**
 * Gets the SVG HTML string for document link icon (for innerHTML usage)
 * @returns SVG HTML string
 */
export const getDocumentLinkIconHtml = (): string => {
  return '<svg style="width:14px;height:14px;vertical-align:middle;margin-right:4px" viewBox="0 0 24 24"><path fill="currentColor" d="M10.59 13.41c.41.39.41 1.03 0 1.42-.39.39-1.03.39-1.42 0a5.003 5.003 0 0 1 0-7.07l3.54-3.54a5.003 5.003 0 0 1 7.07 0 5.003 5.003 0 0 1 0 7.07l-1.49 1.49c.01-.82-.12-1.64-.4-2.42l.47-.48a2.982 2.982 0 0 0 0-4.24 2.982 2.982 0 0 0-4.24 0l-3.53 3.53a2.982 2.982 0 0 0 0 4.24m2.82-4.24c.39-.39 1.03-.39 1.42 0a5.003 5.003 0 0 1 0 7.07l-3.54 3.54a5.003 5.003 0 0 1-7.07 0 5.003 5.003 0 0 1 0-7.07l1.49-1.49c-.01.82.12 1.64.4 2.43l-.47.47a2.982 2.982 0 0 0 0 4.24 2.982 2.982 0 0 0 4.24 0l3.53-3.53a2.982 2.982 0 0 0 0-4.24.973.973 0 0 1 0-1.42"/></svg>'
}

/**
 * Gets the SVG HTML string for image icon (for innerHTML usage)
 * @returns SVG HTML string
 */
export const getImageIconHtml = (): string => {
  return '<svg style="width:14px;height:14px;vertical-align:middle;margin-right:4px" viewBox="0 0 24 24"><path fill="currentColor" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2M8.5 13.5l2.5 3.01L14.5 12l4.5 6H5z"/></svg>'
}

/**
 * Gets the SVG HTML string for video icon (for innerHTML usage)
 * @returns SVG HTML string
 */
export const getVideoIconHtml = (): string => {
  return '<svg style="width:14px;height:14px;vertical-align:middle;margin-right:4px" viewBox="0 0 24 24"><path fill="currentColor" d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2m-8 12.5v-9l6 4.5z"/></svg>'
}
